import { Injectable } from '@angular/core';
import { DataTaskService } from '../data-task.service';

export interface MenuItem {
    item_id?: string;
    parent_id?: string;
    link_id?: string;
    item_name?: string;
    item_state?: string;
    item_parms?: string;
    orderby?: string;
    item_glyphicon?: string;
    item_glyphicon_color?: string;
    item_filtervalue?: string;
    badgeinfo?: string;
    word_id?: string;
    webpage_name?: string;
    p2_approute_keyno?: string;
    menugrp_keyno?: string;
}

export interface Dt53Parms {
    editMode?: boolean;
    webpageName?: string;
    url?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DataTask0To99Service {
    // #region Fields

    /**
     * Session cache for menu groups.
     * BJS 20260310 - Modified to use a Record
     */
    private webpagemenues: Record<string, MenuItem[]> = {};

    /**
     *  BJS 20260310
     *  Keeps track of any current request to prevent sending multiple concurrent requests.
     */
    private webpagemenuRequests: Partial<Record<string, Promise<MenuItem[]>>> = {};

    // #endregion

    // #region Constructor

    constructor(private datatask: DataTaskService) { }

    // #endregion

    // #region Public Methods

    /**
     * Retrieves a list of webpagemenu items for the webpagemenu with the given webpage name.
     *
     * BJS 20260310 - Added editMode as parameter and made service downgrade injectable to avoid
     *                having a separate call to datatask 53 in the angular ttWebPageMenuService.
     *                Also modified key creation to cache separately depending on editMode and
     *                modifed so it will wait for any currently running calls with the same key
     *                instead of sending a new call.
     *
     * DataTask: 53 - sp_ws_p2_webpagemenu_list
     *
     * @param webpageName the name of the webpagemenu to retrieve menu items for.
     * @param editMode
     * @returns a promise containing the webpagemenu items.
     */
    public async dt53(parms: Dt53Parms): Promise<MenuItem[]> {
        if (!parms.webpageName) throw Error('WebpageName is required to retrieve menu items.');

        const normalizedUrl = this.normalizeUrl(parms.url);
        const cacheKey = this.getDt53CacheKey(parms.webpageName, parms.editMode || false, normalizedUrl);

        const cachedItems = this.webpagemenues[cacheKey];

        if (Array.isArray(cachedItems)) {
            return cachedItems;
        }

        const existingRequest = this.webpagemenuRequests[cacheKey];

        if (existingRequest) {
            return existingRequest;
        }

        const request = this.datatask.Post(53, {
            webpage_name: parms.webpageName,
            edit_mode: parms.editMode,
            url: normalizedUrl || null
        }).then(result => {
            this.webpagemenues[cacheKey] = result;
            return result;
        }).finally(() => {
            delete this.webpagemenuRequests[cacheKey];
        });

        this.webpagemenuRequests[cacheKey] = request;

        return request;
    }

    // #endregion

    // #region Private Methods

    private normalizeUrl(url: string | null | undefined): string {
        if (!url)
            return '';

        url = url.trim().toLowerCase();

        while (url.length > 1 && url.endsWith('/')) {
            url = url.slice(0, -1);
        }

        return url;
    }

    private getStringHash(value: string): string {
        let hash = 0;

        for (let i = 0; i < value.length; i++) {
            hash = ((hash << 5) - hash) + value.charCodeAt(i);
            hash |= 0;
        }

        return hash.toString();
    }

    private getDt53CacheKey(webpageName: string, editMode: boolean, url: string | null | undefined): string {
        const normalizedUrl = this.normalizeUrl(url);
        const urlHash = normalizedUrl ? this.getStringHash(normalizedUrl) : 'no-url';

        return `${webpageName}__${editMode ? 'edit' : 'view'}__${urlHash}`;
    }

    // #endregion
}
